/**
 * useSamplePhotoUpload — single source of truth for uploading a photo to a
 * sample round. Shared by the gallery strip (SampleRoundPhotos) and the cover
 * thumbnail (SampleRow) so the HEIC-convert + size-check + base64 flow lives in
 * exactly one place.
 *
 * Returns `selectFile(raw)` (call with a picked File) and an `uploading` flag.
 * Callers own the hidden <input> and clear its value; this hook owns the
 * transform + mutation + toasts.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import { maybeConvertHeic } from '@/lib/heic-convert';
import { useProductDevMutations } from './useProductDev';

// Include HEIC/HEIF so the picker doesn't block iPhone photos before we get a
// chance to convert them (browsers sometimes report an empty mime, so the
// extension entries matter too).
export const SAMPLE_PHOTO_ACCEPTED =
  'image/png,image/jpeg,image/jpg,image/webp,image/gif,image/heic,image/heif,.heic,.heif';
export const SAMPLE_PHOTO_MAX_BYTES = 8 * 1024 * 1024; // 8 MB

export function useSamplePhotoUpload(sampleRoundId: string) {
  const { t } = useTranslation('productDev');
  const { uploadSamplePhoto } = useProductDevMutations();
  const [uploading, setUploading] = useState(false);

  const selectFile = async (raw: File) => {
    // Convert iPhone HEIC/HEIF to JPEG BEFORE the size check (conversion can
    // change the byte size). On failure the original still uploads.
    const { file, failed } = await maybeConvertHeic(raw);
    if (failed) {
      toast({
        title: t('samples.toast.heicFailed'),
        description: t('samples.toast.heicFailedDesc'),
      });
    }
    if (file.size > SAMPLE_PHOTO_MAX_BYTES) {
      toast({
        title: t('samples.toast.fileTooLarge'),
        description: t('samples.toast.fileTooLargeDesc'),
        variant: 'destructive',
      });
      return;
    }
    setUploading(true);
    try {
      // base64-encode the file body — same pattern as variant photos.
      const buffer = await file.arrayBuffer();
      // Chunked btoa to avoid stack overflow on large files.
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(
          null,
          Array.from(bytes.subarray(i, i + chunk)),
        );
      }
      const base64 = btoa(binary);

      await uploadSamplePhoto.mutateAsync({
        sampleRoundId,
        fileName: file.name,
        contentType: file.type || 'image/jpeg',
        body: base64,
      });
    } catch (err) {
      toast({
        title: t('samples.toast.uploadFailed'),
        description: err instanceof Error ? err.message : t('samples.toast.tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  return { uploading, selectFile };
}
